<!-- eslint-disable vue/no-use-v-if-with-v-for -->
<template>
  <div class="mt-6">
    <Tooltip v-if="isOfficeLogin" :content='$t("uploader.form.upload_inactive")' :class="{ 'mb-6': isOfficeLogin }" />
    <h4 v-if="hasSentFileInGroupedFilesArray || sentGroups?.files?.length"
      class="uploaded-files__subtitle mb-6">
      Wysłane pliki
    </h4>
    <section class="uploaded-files__section">
      <div v-for="(group, index) in groupedFilesArray" :key="index" v-if="group.sent_at !== 'not_sent'">
        <UploadedFiles :files="group.files" :sentAt="group.sent_at" :isSent="group.sent_at !== 'not_sent'"
          @uploaded-sizes="getFilesSize" />
      </div>
    </section>

    <transition name="page" mode="out-in">
      <div v-if="notSentGroups[0]?.files?.length || uploads?.length">
        <h4 class="uploaded-files__subtitle mt-6 mb-6">
          Wgrane pliki
        </h4>
        <section class="uploaded-files__section">
          <div v-for="(group, index) in notSentGroups" :key="index"
            v-if="group.sent_at === 'not_sent' && group?.files?.length">
            <UploadedFiles :files="group.files" :sentAt="group.sent_at" :isSent="group.sent_at !== 'not_sent'"
              @uploaded-sizes="getFilesSize">
            </UploadedFiles>
          </div>
          <section class="uploaded-files__uploads" v-if="uploads?.length">
            <UploaderFile :endpoint="validateType(upload.file)" v-for="(upload, index) in uploads" :key="index"
              :upload="upload" :currentFilesSize="sumFilesSize" :maxFilesSize="Number(sumFilesLimit)"
              @progress="handleUploadProgress" @change="handleUploadChange" @remove-file="removeFromUploads"
              @get-file-id="setUploadedFileId" @cancel-upload="cancelUpload" />
          </section>
        </section>
      </div>
    </transition>

    <UploaderForm class="mt-6" v-if="showUploader || (uploadedHomeworks.files && !uploadedHomeworks.is_sent)"
      :currentFilesSize="sumFilesSize" @chosen="handleFilesChosen" />

    <div v-if="sumFilesSize !== 0" class="size-counter">
      <span class="additional-info">
        <p>
          Wykorzystano {{ formatFileSize(sumFilesSize) }} / {{ formatFileSize(sumFilesLimit) }}.
          Obsługiwane rodzaje plików: .pdf, .jpg, .jpeg, .png, .doc, .docx, .zip, .rar, .mp3
        </p>
        {{ $t("dashboard.uploaded_files_info") }}
      </span>
    </div>

    <div class="flex flex-col items-center justify-center mt-6">
      <Button v-if="(!uploads || !uploads?.length && uploadedHomeworks.is_sent && !showUploader) && !isHistoricalAccess"
        class="btn_primary btn_outlined btn-xl mt-2 clear-homeworks" @click="handleShowUploader">
        Wgraj dodatkowe pliki
      </Button>
      <Button v-if="uploads && uploads.length || notSentGroups[0]?.files?.length"
        :disabled="!uploads.length && !uploadedHomeworks?.files?.length || loading || isSent" class="btn_primary btn_send mt-2"
        @click="sendHomeworks">
        {{ $t("uploader.form.send_homeworks") }}
        <span v-if="loading" class="loader w-3 h-3"></span>
      </Button>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import swal from "sweetalert2/dist/sweetalert2.js";
import { mapActions, mapState, mapGetters } from "vuex";
import { v4 as uuid } from "uuid";
import states from "./states";

import Button from "@/components/Form/BaseButton.vue";
import Tooltip from "@/components/Tooltip/Tooltip.vue";

import UploaderForm from "./UploaderForm.vue";
import UploaderFile from "./UploaderFile.vue";
import UploadedFiles from "./UploadedFiles.vue";

import uploadedHomeworks from "../../store/uploaded-homeworks";
import HomeworkUploadedList from "../Homeworks/HomeworkUploadedList.vue";

import ErrorToast from "../Toasts/ErrorToast.vue";
import SuccessToast from "../Toasts/SuccessToast.vue";

export default {
  components: {
    Button,
    Tooltip,
    UploaderForm,
    UploaderFile,
    UploadedFiles,
    HomeworkUploadedList,
  },

  data() {
    return {
      loading: false,
      blocked: false,
      uploads: [],
      maxConcurrentUploads: 3,
      isSent: false,
      sumFilesSize: 0,
      sumFilesLimit: process.env.VUE_APP_UPLOAD_LIMIT,
      showUploader: false,
    };
  },

  computed: {
    ...mapGetters("homeworks", ["getHomeworkById"]),
    ...mapGetters('auth', ['isOfficeLogin']),
    ...mapState("uploadedHomeworks", [
      "uploadedHomeworks",
      "isUploadedHomeworksSent",
    ]),
    ...mapState("students", ["student"]),
    ...mapState("auth", ["user"]),

    currentUploadCount() {
      if (!this.uploads) { return undefined }
      return this.uploads.filter((upload) => upload.uploading).length;
    },

    homework() {
      return this.getHomeworkById(Number(this.$route.params.id));
    },

    studentFullName() {
      return `${this.student.firstName} ${this.student.lastName}`;
    },

    lectorFullName() {
      return `${this.student.group.lector.firstName} ${this.student.group.lector.lastName}`;
    },

    isHistoricalAccess() {
      return this.student?.isArchived;
    },

    groupedFilesArray() {
      if (!this.uploadedHomeworks?.files) {
        return [];
      }

      const groupedFiles = this.uploadedHomeworks.files.reduce((acc, file) => {
        const sentAt = file.sent_at || "not_sent";

        if (!acc[sentAt]) {
          acc[sentAt] = {
            sent_at: sentAt,
            files: []
          };
        }

        acc[sentAt].files.push(file);

        return acc;
      }, {});

      return Object.values(groupedFiles);
    },

    notSentGroups() {
      return this.groupedFilesArray
        .filter(group => group.sent_at === null || group.sent_at === 'not_sent')
        .map(group => ({
          ...group,
          files: group.files.filter(file => !file.is_deleted)
        }));
    },

    sentGroups() {
      return this.groupedFilesArray
        .filter(group => group.sent_at === 'sent')
        .map(group => ({
          ...group,
          files: group.files.filter(file => !file.is_deleted)
        }));
    },

    hasSentFileInGroupedFilesArray() {
      const hasSentItem = this.groupedFilesArray.some(item =>
        (item.files && item.files.some(file => file.sent_at !== "not_sent" && file.sent_at !== null))
      );

      return hasSentItem;
    },

    homeworkTitle() {
      return this.homework.lesson.homeworkSubject
        ? this.homework.lesson.homeworkSubject
        : this.$t("dashboard.empty_homework_title");
    },

    additionalInfo() {
      return this.$t('uploader.form.old_files_homeworks');
    }
  },

  methods: {
    ...mapActions("uploadedHomeworks", [
      "getUploadedHomeworks",
      "updatedUploadHomeworks",
      "sendUploadedHomeworks",
      "removeHomework",
      "clearUploadedHomeworks",
    ]),

    handleUploadChange(e) {
      switch (e.state) {
        case states.UPLOADING:
          this.uploads = this.uploads.map((upload) => {
            if (e.id === upload.id) {
              upload.uploading = true;
            }

            return upload;
          });
          break;
        case states.COMPLETE:
          this.uploads = this.uploads.map((upload) => {
            if (e.id === upload.id) {
              upload.complete = true;
              upload.uploading = false;
              this.sumFilesSize += upload.size;
              this.isSent = false;
              this.$emit("sent-files", this.isSent);
            }

            return upload;
          });
          break;
        case states.CANCELLED:
          this.uploads = this.uploads.map((upload) => {
            if (e.id === upload.id) {
              upload.cancelled = true;
              upload.uploading = false;
            }

            return upload;
          });
          break;
        case states.FAILED:
          this.uploads = this.uploads.map((upload) => {
            if (e.id === upload.id) {
              upload.failed = true;
              upload.uploading = false;
            }

            this.$toast({
              component: ErrorToast,
              props: {
                title: 'Błąd wgrywania pliku',
              },
            }, {
              type: 'error'
            });

            return upload;
          });
          break;
      }
    },

    handleUploadProgress(e) {
      this.uploads = this.uploads.map((upload) => {
        if (e.id === upload.id) {
          upload.progress = e.progress;
        }
        return upload;
      });
    },

    validateType(file) {
      const fileType = file.type;
      const fileName = file.name;
      const acceptableFormats = [
        "image/jpeg",
        "image/bmp",
        "image/png",
        "audio/mpeg",
        "application/msword",
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/x-zip-compressed",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (acceptableFormats.includes(fileType)) {
        return "homework/files/upload";
      }
      if (
        fileName.includes(".rar") ||
        fileName.includes(".docx") ||
        fileName.includes(".xlsx")
      ) {
        return "homework/files/upload";
      }
      return null;
    },

    handleFilesChosen(files) {
      const isFileTypeSuppored = this.validateType(files[0]);

      if (!isFileTypeSuppored) {
        this.$toast({
          component: ErrorToast,
          props: {
            title: 'Nieobsługiwany typ pliku',
          },
        }, {
          type: 'error'
        });
        return null;
      }

      this.uploads.push(
        ...Array.from(files).map((file) => ({
          id: uuid(),
          progress: 0,
          uploading: false,
          complete: false,
          cancelled: false,
          failed: false,
          queued: true,
          file,
          size: file.size,
        }))
      );
    },

    removeUploadedFiles() {
      this.sumFilesSize = 0;
      for (let i = 0; i < this.uploadedHomeworks?.file?.length; i++) {
        this.removeHomework({
          student_id: this.uploadedHomeworks?.files[i].student_id,
          homework_id: this.uploadedHomeworks?.files[i].homework_id,
          file_id: this.uploadedHomeworks?.files[i].id,
        });
      }
      this.clearUploadedHomeworks();
    },

    async removeFromUploads(file) {
      const file_id = this.uploads.find((upload) => upload.id === file.id)
        .file_id;
      await this.removeHomework({
        student_id: this.student.id,
        homework_id: this.homework.lesson.id,
        file_id: file_id,
      });
      const index = this.uploads.map((upload) => upload.id).indexOf(file.id);
      this.uploads.splice(index, 1);
      if (this.uploads.length < 1) {
        this.$emit("sent-files", true);
      }
      this.sumFilesSize -= file.size;
    },

    cancelUpload(file) {
      const index = this.uploads.map((upload) => upload.id).indexOf(file.id);
      this.uploads.splice(index, 1);
    },

    setUploadedFileId(data) {
      this.uploads = this.uploads.map((item) =>
        item.id === data.id ? { ...item, file_id: data.file_id } : item
      );
    },

    async sendHomeworks() {
      if (!this.uploads.length && !this.uploadedHomeworks?.files?.length) {
        this.emptyListErrorModal();
        return;
      }
      if (!this.blocked) {
        this.blockRequestsFor(1000);
        try {
          this.loading = true;
          await this.sendUploadedHomeworks({
            student_id: this.student.id,
            student_name: this.studentFullName,
            student_email: this.user.email,
            student_group: this.student.group.parentFullName,
            homework_id: this.homework.lesson.id,
            homework_title: this.homeworkTitle,
            homework_date: this.homework.lesson.date,
            teacher_email: this.student.group.lector.email,
            teacher_name: this.lectorFullName,
          });
          this.isSent = true;
          this.$emit("sent-files", this.isSent);
          this.loading = false;
          this.successModal();
        } catch (e) {
          console.error(e);
          this.loading = false;
          if (e?.response?.status && e?.response?.status === 424) {
            const isMailgunError = true;
            return this.errorModal(isMailgunError);
          }
          this.errorModal();
        }
      }
    },

    blockRequestsFor(blockDuration) {
      this.blocked = true;
      setTimeout(() => {
        this.blocked = false;
      }, blockDuration);
    },

    async successModal() {
      this.$toast({
        component: SuccessToast,
        props: {
          title: 'Praca domowa została wysłana do lektora',
        },
      }, {
        type: 'success',
        onClose: () => {
          this.$router.go();
        }
      });
    },

    async errorModal(isMailgunError = false) {
      this.$toast({
          component: ErrorToast,
          props: {
            title: 'Błąd połączenia w trakcie wysyłania',
            description: 'Pliki nie zostały wysłane do lektora. Spróbuj ponownie za chwilę'
          },
        }, {
          type: 'error'
        });
    },

    async emptyListErrorModal() {
      await swal.fire({
        title: this.$t("modal.error_empty_list.title"),
        text: this.$t("modal.error_empty_list.text"),
        confirmButtonText: this.$t("modal.error_empty_list.confirm"),
        buttonsStyling: false,
        icon: "error",
        allowOutsideClick: false,
        customClass: {
          confirmButton: "swal2-confirm-custom",
        },
      });
    },

    getFilesSize(size) {
      this.sumFilesSize = size;
    },

    formatFileSize(fileSize) {
      const fileExtension = ["Bytes", "KB", "MB", "GB"];
      let i = 0;
      while (fileSize > 900) {
        fileSize /= 1024; // eslint-disable-line no-param-reassign
        i += 1;
      }
      return `${Math.round(fileSize * 100) / 100} ${fileExtension[i]}`;
    },

    handleShowUploader() {
      this.showUploader = true;
    },
  },

  mounted() {
    setInterval(() => {
      if (this.currentUploadCount >= this.maxConcurrentUploads) {
        return;
      }

      const queued = this.uploads.filter((upload) => upload.queued === true);

      if (queued.length) {
        queued[0].queued = false;
      }
    }, 1000);
  },

  async created() {
    await this.getUploadedHomeworks({
      studentId: this.student.id,
      homeworkId: this.$route.params.id,
    });
  },
};
</script>

<style lang="scss" scoped>
.loader {
  width: 24px;
  height: 24px;
  border: 3px solid #FFF;
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.btn_send {
  display: flex;
  align-items: center;
  gap: 8px;
}

.size-counter {
  @apply flex justify-start mt-3 mb-10 text-xs;
}

.sent-files-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.uploaded-files-wrapper {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 24px;
  width: 100%;
  margin-bottom: 24px;

  @media screen and (min-width: 1280px) {
    grid-template-columns: 1fr 1fr;
  }
}

.uploaded-files__section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.uploaded-files__uploads {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 24px;
  width: 100%;
  background-color: #fafafc;
  border: 1px solid #f3f5f8;
  border-radius: 8px;
  padding: 18px;

  @media screen and (min-width: 1280px) {
    padding: 32px;
  }

  @media screen and (min-width: 1280px) {
    grid-template-columns: 1fr 1fr;
  }
}

input {
  transition: all 0.5s ease;
}

button {
  position: relative;
  transition: all 0.5s ease;
}

.additional-info {
  color: #2E384A;
  font-family: "GT Walsheim Pro";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.btn_primary {
  @apply text-base p-0 px-6;
  line-height: 44px;
}

.btn_outlined {
  background-color: #ffffff;
  color: #E10A7B;
  border: 2px solid #E10A7B;

  &:hover {
    color: #ffffff
  }
}

.animate-spin {
  animation: spin 1.5s linear 0s infinite;
}

@keyframes spin {
  from {
    transform: rotate(0);
  }

  to {
    transform: rotate(360deg);
  }
}

.fade-animation-enter-active {
  transition: all 0.4s ease;
}

.fade-animation-leave-active {
  transition: all 0.4s cubic-bezier(1, 0.5, 0.8, 1);
}

.fade-animation-enter,
.fade-animation-leave-to {
  transform: translateY(-5px);
  opacity: 0;
}

.upload-info.inactive {
  color: #bababa;
}

.uploader__files {
  @apply mt-6;

  &:not(:empty) {
    @apply mb-6;
  }
}

.uploaded-files__subtitle {
  color: var(--grey-900, #111724);

  /* H4 */
  font-family: "GT Walsheim Pro";
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
}

.uploader__description {
  color: var(--700, #2E384A);
  font-family: "GT Walsheim Pro";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  display: block;
  margin-top: 12px;
}

.clear-homeworks {
  white-space: nowrap;
}
</style>
